<script setup lang="ts">
import {
	Button,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	SingleSelectList,
	SingleSelectListItem,
} from '@laam/ui/base';
import { type Product, productInjectKey } from '~/types/Product.ts';
import { useFetchSizeChart } from '~/data/product.ts';
import { PhCaretRight } from '@phosphor-icons/vue';
import CustomTrouserForm from '~/components/product-detail-v2/size-chart/CustomTrouserForm.vue';
import CustomShirtForm from '~/components/product-detail-v2/size-chart/CustomShirtForm.vue';
import HowToMeasureFigure from '~/components/product-detail-v2/size-chart/HowToMeasure.vue';
import SizeTableComponent from '~/components/product-detail-v2/size-chart/SizeTableComponent.vue';

interface SizeChartModalProps {
	size?: string;
}
const props = defineProps<SizeChartModalProps>();
const isOpen = ref(false);

const productData = inject<Product>(productInjectKey);

const sizeChartId = computed(() =>
	productData ? productData.size_chart_id : 0,
);

let result;
if (props.size && sizeChartId.value !== 0) {
	result = await useFetchSizeChart(sizeChartId.value, true);
} else {
	result = await useFetchSizeChart(sizeChartId.value, isOpen);
}

const sizeChartData = computed(() => {
	if (result && result.status.value === 'success') {
		return result.data.value;
	} else {
		return {};
	}
});

const extractDataForTable = (sizeChartData: any, sizeTitle?: string) => {
	const sizeDataEntries = Object.entries(sizeChartData);
	const result = [];

	for (const [heading, categoryData] of sizeDataEntries) {
		const sizes = categoryData?.heading?.sizes || [];
		const sizeIndex = sizes.indexOf(sizeTitle);

		if (sizeIndex !== -1 && categoryData?.data) {
			const data = Object.entries(categoryData.data)
				.map(([key, values]) => `${key}: ${values[sizeIndex]}`)
				.join(', ');

			result.push({
				heading: heading.charAt(0).toUpperCase() + heading.slice(1),
				data,
			});
		}
	}

	return result;
};

const sizeData = ref(extractDataForTable(sizeChartData.value, props.size));
const units = ref('in');

function returnUnit(valInInches: string) {
	const inchesWithLabels = valInInches.split(',').map((i) => {
		const [label, inch] = i.split(':');
		return { label, inch: Number(inch) };
	});
	if (String(valInInches).includes('-') && units.value === 'cm') {
		const values = String(valInInches)
			.split('-')
			.map((val) => parseInt(val) * 2.54);
		return `${values[0]}-${values[1]}`;
	}
	if (units.value === 'cm') {
		return inchesWithLabels
			.map(({ label, inch }) => `${label}:${(inch * 2.54).toFixed(1)}`)
			.join(',');
	} else {
		return valInInches;
	}
}
onMounted(() => {});

watch(
	() => props.size,
	(newSize) => {
		sizeData.value = extractDataForTable(sizeChartData.value, newSize);
	},
);

watch(
	() => result.data.value,
	(newData) => {
		if (newData) {
			sizeData.value = extractDataForTable(sizeChartData.value, props.size);
		}
	},
);
</script>

<template>
	<Dialog v-if="!props.size" :open="isOpen" @update:open="isOpen = !isOpen">
		<DialogTrigger
			class="pdp__size-chart-trigger-desktop gap-xs flex items-center text-gray-500"
		>
			<span class="text-sm font-semibold text-gray-800"> Size Chart </span>
			<PhCaretRight
				class="size-3xl text-gray-800"
				:fill="'text-gray-800'"
				:weight="'bold'"
			/>
		</DialogTrigger>
		<DialogContent
			class="product_size_chart-modal rounded-xl min-w-fit w-[50%] h-[90%] overflow-hidden"
		>
			<DialogHeader class="mb-5xl !pt-0">
				<DialogTitle class="mb-xs text-lg font-semibold">
					Size Guide
				</DialogTitle>
			</DialogHeader>

			<Tabs default-value="size-chart" class="size-chart-tabs">
				<TabsList
					class="py-none grid grid-cols-2 bg-white border-b border-gray-200 border-solid rounded-none"
				>
					<TabsTrigger
						value="size-chart"
						class="mx-xl size-chart-tab data-[state=active]:text-gray-700 data-[state=active]:border-gray-700 border-solid text-gray-500 pb-xl rounded-none border-0 shadow-transparent border-b-2 px-xs p-xs border-white"
					>
						Size Chart
					</TabsTrigger>
					<TabsTrigger
						value="guide"
						class="size-chart-tab mx-xl data-[state=active]:text-gray-700 data-[state=active]:border-gray-700 border-solid text-gray-500 pb-xl rounded-none border-0 shadow-transparent border-b-2 px-xs p-xs border-white"
					>
						How to measure
					</TabsTrigger>
				</TabsList>

				<TabsContent value="size-chart" class="pb-7xl h-[72vh] overflow-auto">
					<div v-if="showCustomSizeChart" class="size-chart-form">
						<div class="">
							<form id="customShirtForm" @submit="onSubmit">
								<CustomShirtForm :custom-shirt="customShirt" />
								<div class="border-b border-gray-600" />
								<CustomTrouserForm :custom-trouser="customTrouser" />
							</form>
						</div>
						<div class="">
							<Button variant="secondary" @click="resetForm">
								<span class="text-md font-medium text-gray-800">Reset</span>
							</Button>
							<Button variant="primary" form="customShirtForm">
								<span class="text-md font-medium text-white">Save</span>
							</Button>
						</div>
					</div>

					<div v-else class="size-chart-tables">
						<div
							v-for="(sizeChart, label, index) in sizeChartData"
							:key="index"
						>
							<SizeTableComponent :size-chart="sizeChart" :title="label" />
						</div>
					</div>
				</TabsContent>
				<TabsContent value="guide" class="h-[72vh] overflow-auto">
					<div class="px-3xl py-8xl">
						<HowToMeasureFigure class="pt-lg w-full" />
					</div>
				</TabsContent>
			</Tabs>
		</DialogContent>
	</Dialog>

	<div v-if="sizeData.length !== 0">
		<div
			class="flex w-ful flex-col p-xl bg-gray-50 border-gray-200 border rounded-medium gap-md mb-3xl"
		>
			<div class="gap-xs flex items-center rounded justify-between">
				<span class="text-gray-500 font-medium text-sm">Measurements ({{ units }})</span>
				<SingleSelectList
					v-model="units"
					class="gap-md grid grid-cols-2 rounded-small bg-gray-100 p-xs text-center"
					default-value="in"
				>
					<SingleSelectListItem
						value="cm"
						class="props.sizeChart-[state=checked]:bg-gray-white props.sizeChart-[state=checked]:text-gray-800 py-xs px-sm text-xs rounded"
					>
						cm
					</SingleSelectListItem>
					<SingleSelectListItem
						value="in"
						class="props.sizeChart-[state=checked]:bg-gray-white props.sizeChart-[state=checked]:text-gray-800 py-xs px-sm text-xs rounded"
						:is-size-chart="true"
					>
						in
					</SingleSelectListItem>
				</SingleSelectList>
			</div>
			<div
				v-for="(item, index) in sizeData"
				:key="'row-' + index"
				class="flex flex-col justify-center gap-xs"
			>
				<span class="text-sm font-medium text-gray-800">
					{{ item.heading }}
				</span>
				<span class="font-normal text-xs text-gray-700">
					{{ returnUnit(item.data) }}
				</span>
			</div>
		</div>
	</div>
</template>
